import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Upload, FileText } from 'lucide-react';

const SAMPLE_JSON = `[
  {
    "slug": "downtown-whitby",
    "schools_named": [
      { "name": "Whitby Shores PS", "type": "Elementary", "rating": 7.6, "source": "Fraser Institute 2025" },
      { "name": "Henry Street HS", "type": "Secondary", "rating": 7.8, "source": "Fraser Institute 2025" }
    ]
  }
]`;

const SAMPLE_CSV = `neighbourhood_slug,school_name,school_type,rating,source
downtown-whitby,Whitby Shores PS,Elementary,7.6,Fraser Institute 2025
downtown-whitby,Henry Street HS,Secondary,7.8,Fraser Institute 2025`;

export default function AdminSchools() {
    const { user, loading } = useAuth();
    const [jsonText, setJsonText] = useState('');
    const [csvText, setCsvText] = useState('');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    if (loading) return <div className="p-16 text-neutral-500">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.is_admin) return <div className="max-w-2xl mx-auto p-16 text-neutral-500">Admin only.</div>;

    async function submitJson() {
        if (!jsonText.trim() || busy) return;
        setBusy(true); setResult(null);
        try {
            const parsed = JSON.parse(jsonText);
            const body = Array.isArray(parsed) ? { updates: parsed } : parsed;
            const { data } = await api.post('/admin/schools/update', body);
            setResult(data);
            toast.success(`Updated ${data.updated} neighbourhoods`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Failed');
            setResult({ error: e.message });
        } finally { setBusy(false); }
    }

    async function submitCsv() {
        if (!csvText.trim() || busy) return;
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post('/admin/schools/upload-csv', csvText, {
                headers: { 'Content-Type': 'text/csv' },
            });
            setResult(data);
            toast.success(`Processed ${data.rows_processed} rows — updated ${data.neighbourhoods_updated}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Failed');
            setResult({ error: e.message });
        } finally { setBusy(false); }
    }

    function onCsvFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const r = new FileReader();
        r.onload = () => setCsvText(String(r.result || ''));
        r.readAsText(file);
    }

    return (
        <div>
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-8">
                    <div className="overline">Admin · Data refresh</div>
                    <h1 className="font-editorial text-[40px] lg:text-[52px] tracking-editorial mt-4">School data refresh</h1>
                    <p className="mt-4 text-[14px] text-neutral-600 max-w-2xl">
                        Update Fraser Institute or EQAO school ratings per neighbourhood. Paste JSON, or upload a CSV.
                        Existing school lists are replaced (not merged) per neighbourhood.
                    </p>
                </div>
            </section>

            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* JSON */}
                    <div className="border border-neutral-200 p-6">
                        <div className="flex items-center gap-2 overline"><FileText className="w-3.5 h-3.5" /> JSON paste</div>
                        <p className="text-[13px] text-neutral-600 mt-2">Array of <code className="font-mono-data text-[12px]">{'{slug, schools_named: [...]}'}</code> objects, or the full <code className="font-mono-data text-[12px]">{'{updates: [...]}'}</code> envelope.</p>
                        <textarea
                            data-testid="admin-schools-json"
                            value={jsonText}
                            onChange={(e) => setJsonText(e.target.value)}
                            placeholder={SAMPLE_JSON}
                            rows={16}
                            className="mt-4 w-full border border-neutral-300 px-3 py-2 font-mono-data text-[12px] leading-snug"
                           
                        />
                        <button
                            data-testid="admin-schools-json-submit"
                            onClick={submitJson}
                            disabled={busy}
                            className="mt-4 bg-brand-navy text-white px-5 py-2.5 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy border border-brand-navy disabled:opacity-50"
                           
                        >
                            {busy ? 'Updating…' : 'Apply JSON update'}
                        </button>
                    </div>

                    {/* CSV */}
                    <div className="border border-neutral-200 p-6">
                        <div className="flex items-center gap-2 overline"><Upload className="w-3.5 h-3.5" /> CSV upload</div>
                        <p className="text-[13px] text-neutral-600 mt-2">Columns: <code className="font-mono-data text-[12px]">neighbourhood_slug,school_name,school_type,rating,source</code></p>
                        <input type="file" accept=".csv,text/csv" onChange={onCsvFile}
                            data-testid="admin-schools-csv-file"
                            className="mt-4 block text-[13px]" />
                        <textarea
                            data-testid="admin-schools-csv"
                            value={csvText}
                            onChange={(e) => setCsvText(e.target.value)}
                            placeholder={SAMPLE_CSV}
                            rows={14}
                            className="mt-4 w-full border border-neutral-300 px-3 py-2 font-mono-data text-[12px] leading-snug"
                           
                        />
                        <button
                            data-testid="admin-schools-csv-submit"
                            onClick={submitCsv}
                            disabled={busy}
                            className="mt-4 bg-brand-navy text-white px-5 py-2.5 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy border border-brand-navy disabled:opacity-50"
                           
                        >
                            {busy ? 'Updating…' : 'Apply CSV update'}
                        </button>
                    </div>
                </div>

                {result && (
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16">
                        <div className="border border-brand-navy bg-[#F7F7F5] p-6">
                            <div className="overline mb-3">Result</div>
                            <pre className="font-mono-data text-[12px] leading-snug whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
